import { Subscription, SubscriptionPlan } from '../types/subscription';
import { getPlanById } from '../constants/subscriptionPlans';
import { TrialExtensionUtils, TRIAL_EXTENSION_CONSTANTS } from '../constants/trialExtension';

// Subscription utility functions
export class SubscriptionUtils {
  // Check if subscription is in trial period
  static isInTrialPeriod(subscription: Subscription): boolean {
    // Check if subscription status is 'trial'
    if (subscription.status === 'trial') {
      // If endDate exists, check if trial is still valid
      if (subscription.endDate) {
        const now = Date.now();
        const trialEnd = parseInt(subscription.endDate);
        return now < trialEnd;
      }
      // If no endDate but status is 'trial', consider it a trial
      return true;
    }
    
    return false;
  }

  // Get remaining trial days with support for extended trials
  static getTrialDaysRemaining(subscription: Subscription): number {
    if (subscription.status !== 'trial') {
      return 0;
    }
    
    const now = new Date();
    const startDate = new Date(parseInt(subscription.startDate));
    
    // Check if this is an extended trial based on trialExtensionReason
    const isExtendedTrial = subscription.trialExtensionReason === 'business_extension' || 
      (subscription.endDate && 
       parseInt(subscription.endDate) === TRIAL_EXTENSION_CONSTANTS.EXTENDED_TRIAL_END_DATE.getTime());
    
    if (isExtendedTrial) {
      // Use extended trial calculation
      return TrialExtensionUtils.getExtendedTrialDaysRemaining(now);
    } else {
      // Use standard trial calculation
      return TrialExtensionUtils.getStandardTrialDaysRemaining(startDate, now);
    }
  }

  // Check if subscription is active (not expired, cancelled, or past due)
  static isActive(subscription: Subscription): boolean {
    if (!subscription) return false;
    
    const now = Date.now();
    const endDate = parseInt(subscription.endDate);
    
    return subscription.status === 'active' && now < endDate;
  }

  // Check if subscription is expired
  static isExpired(subscription: Subscription): boolean {
    if (!subscription) return true;
    
    const now = Date.now();
    const endDate = parseInt(subscription.endDate);
    
    return now >= endDate;
  }

  // Get subscription status with trial consideration
  static getEffectiveStatus(subscription: Subscription): string {
    if (!subscription) return 'expired';
    
    // If in trial period, return trial status
    if (this.isInTrialPeriod(subscription)) {
      return 'trial';
    }
    
    // If expired, return expired
    if (this.isExpired(subscription)) {
      return 'expired';
    }
    
    // Return the actual status
    return subscription.status;
  }

  // Calculate subscription end date based on plan (returns millis)
  static calculateEndDate(planId: string, startDateMillis: number): number {
    const plan = getPlanById(planId);
    if (!plan) {
      // Default to monthly if plan not found
      return TrialExtensionUtils.getPaidSubscriptionEndDate(new Date(startDateMillis), 'monthly').getTime();
    }
    
    const billingCycle = plan.billingCycle === 'yearly' ? 'yearly' : 'monthly';
    return TrialExtensionUtils.getPaidSubscriptionEndDate(new Date(startDateMillis), billingCycle).getTime();
  }

  // Calculate trial end date (returns millis) - use centralized logic
  static calculateTrialEndDate(startDateMillis: number, trialDays: number): number {
    // For backward compatibility, but prefer using TrialExtensionUtils.getTrialEndDate
    const registrationDate = new Date(startDateMillis);
    return TrialExtensionUtils.getTrialEndDate(registrationDate).getTime();
  }

  // Create subscription with proper trial handling
  static createSubscription(
    userId: string,
    planId: string,
    startDateMillis?: number
  ): Omit<Subscription, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'> {
    const now = startDateMillis || Date.now();
    const plan = getPlanById(planId);
    
    if (!plan) {
      throw new Error(`Plan ${planId} not found`);
    }

    const isInTrialPeriod = (plan.trialDays || 0) > 0;
    const endDate = this.calculateEndDate(planId, now);
    const status = isInTrialPeriod ? 'trial' : 'active';

    return {
      userId,
      userName: '',
      userPhone: '',
      planId,
      status: status as any,
      startDate: now.toString(),
      endDate: endDate.toString(),
      totalPrice: plan.price,
      currency: 'EGP',
    };
  }

  // Create subscription with extended trial support
  static createSubscriptionWithExtendedTrial(
    userId: string,
    planId: string,
    registrationDate: Date = new Date(),
    startDateMillis?: number
  ): Omit<Subscription, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'> {
    const now = startDateMillis || Date.now();
    const plan = getPlanById(planId);
    
    if (!plan) {
      throw new Error(`Plan ${planId} not found`);
    }

    // Determine if this should be an extended trial
    const shouldGetExtendedTrial = TrialExtensionUtils.shouldGetExtendedTrial(registrationDate);
    const isInTrialPeriod = true; // Always in trial for this method
    const endDate = TrialExtensionUtils.getTrialEndDate(registrationDate);
    const status = 'trial';

    return {
      userId,
      userName: '',
      userPhone: '',
      planId,
      status: status as any,
      startDate: now.toString(),
      endDate: endDate.getTime().toString(),
      totalPrice: 0, // Trial is free
      currency: 'EGP',
      billingCycle: 'monthly',
      isActive: true,
      trialExtensionReason: shouldGetExtendedTrial ? 'business_extension' : 'standard_trial'
    };
  }

  // Check if subscription is an extended trial based on trialExtensionReason
  static isExtendedTrial(subscription: Subscription): boolean {
    return Boolean(subscription.trialExtensionReason === 'business_extension' || 
      (subscription.endDate && 
       parseInt(subscription.endDate) === TRIAL_EXTENSION_CONSTANTS.EXTENDED_TRIAL_END_DATE.getTime()));
  }

  // Get trial type information
  static getTrialType(subscription: Subscription): 'extended' | 'standard' | 'none' {
    if (subscription.status !== 'trial') {
      return 'none';
    }
    
    return this.isExtendedTrial(subscription) ? 'extended' : 'standard';
  }


  // Check if user can access a feature
  static canAccessFeature(subscription: Subscription, feature: string): boolean {
    if (!subscription) return false;
    
    const plan = getPlanById(subscription.planId);
    if (!plan) return false;

    // If in trial period, allow access to all features
    if (this.isInTrialPeriod(subscription)) {
      return true;
    }

    // Check feature access based on plan
    switch (feature) {
      case 'eventReminders':
        return plan.includesEventReminders;
      case 'advancedStats':
        return plan.includesAdvancedStats;
      case 'community':
        return plan.includesCommunity;
      case 'phoneSupport':
        return plan.includesPhoneSupport;
      case 'teamManagement':
        return plan.teamMemberPrice !== undefined;
      default:
        return false;
    }
  }

  // Get subscription summary for display
  static getSubscriptionSummary(subscription: Subscription): {
    planName: string;
    status: string;
    isTrial: boolean;
    trialDaysRemaining: number;
    expiresAt: string;
    price: number;
  } {
    const plan = getPlanById(subscription.planId);
    const isTrial = this.isInTrialPeriod(subscription);
    const trialDaysRemaining = this.getTrialDaysRemaining(subscription);
    
    return {
      planName: plan?.name || 'Unknown Plan',
      status: this.getEffectiveStatus(subscription),
      isTrial,
      trialDaysRemaining,
      expiresAt: subscription.endDate,
      price: subscription.totalPrice
    };
  }
}

export default SubscriptionUtils;
